import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Upload, Trash2, LogOut, ExternalLink, ImagePlus, Loader2, Pencil, Check, X } from "lucide-react";
import { API } from "@/config";
import AppointmentsPanel from "@/components/admin/AppointmentsPanel";
import TodayPanel from "@/components/admin/TodayPanel";
import ServicesPanel from "@/components/admin/ServicesPanel";
import SchedulePanel from "@/components/admin/SchedulePanel";
import TelegramPanel from "@/components/admin/TelegramPanel";
import CalendarPanel from "@/components/admin/CalendarPanel";
import ClientsPanel from "@/components/admin/ClientsPanel";
import ReviewsPanel from "@/components/admin/ReviewsPanel";
import NewsPanel from "@/components/admin/NewsPanel";

const TABS = [
  { id: "hoy", label: "Hoy" },
  { id: "citas", label: "Citas" },
  { id: "clientas", label: "Mis clientas" },
  { id: "fotos", label: "Fotos" },
  { id: "servicios", label: "Servicios y precios" },
  { id: "horario", label: "Horario" },
  { id: "resenas", label: "Reseñas" },
  { id: "novedades", label: "Novedades" },
  { id: "telegram", label: "Avisos Telegram" },
  { id: "calendario", label: "Agenda iPhone" },
];

const TOKEN_KEY = "isi_admin_token";

function Login({ onSuccess }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { password });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      toast.success("Bienvenida, Isi");
      onSuccess(res.data.token);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo iniciar sesión");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login" className="min-h-screen bg-cream sunset-glow flex items-center justify-center px-4">
      <form
        onSubmit={submit}
        className="w-full max-w-sm rounded-3xl bg-creamcard border border-sand shadow-xl shadow-gold/10 p-10"
      >
        <div className="w-14 h-14 rounded-full bg-rosesoft flex items-center justify-center mx-auto">
          <Lock className="w-6 h-6 text-gold" />
        </div>
        <h1 className="font-display text-2xl sm:text-3xl font-semibold text-center text-obsidian mt-6">
          Panel de ISI'S Nails
        </h1>
        <p className="text-sm text-taupe text-center mt-2">Acceso exclusivo para la propietaria</p>
        <input
          type="password"
          data-testid="admin-password-input"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Contraseña"
          autoFocus
          className="mt-8 w-full rounded-xl border border-sand bg-cream px-4 py-3 text-sm text-obsidian placeholder:text-taupe/60 focus:outline-none focus:ring-2 focus:ring-gold"
        />
        <button
          type="submit"
          data-testid="admin-login-submit"
          disabled={loading || !password}
          className="mt-4 w-full rounded-xl bg-gold hover:bg-gold-dark disabled:opacity-50 text-white text-sm font-medium py-3 transition-colors duration-200 flex items-center justify-center gap-2"
        >
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          Entrar
        </button>
        <Link to="/" className="block text-center text-xs text-taupe hover:text-gold mt-6 transition-colors">
          ← Volver al sitio
        </Link>
      </form>
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const [photos, setPhotos] = useState([]);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [title, setTitle] = useState("");
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const inputRef = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };
  const [tab, setTab] = useState("hoy");

  const load = () =>
    axios
      .get(`${API}/photos`)
      .then((res) => setPhotos(res.data))
      .catch(() => toast.error("No se pudieron cargar las fotos"));

  useEffect(() => {
    load();
  }, []);

  const pickFile = (f) => {
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("title", title);
      await axios.post(`${API}/admin/photos`, form, { headers });
      toast.success("Foto publicada en tu galería");
      setFile(null);
      setPreview(null);
      setTitle("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo subir la foto");
    } finally {
      setUploading(false);
    }
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setEditTitle(p.title || "");
  };

  const saveEdit = async (id) => {
    try {
      const res = await axios.put(`${API}/admin/photos/${id}`, { title: editTitle }, { headers });
      setPhotos((prev) => prev.map((p) => (p.id === id ? res.data : p)));
      setEditingId(null);
      toast.success("Título actualizado");
    } catch {
      toast.error("No se pudo guardar el cambio");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta foto de la galería?")) return;
    try {
      await axios.delete(`${API}/admin/photos/${id}`, { headers });
      setPhotos((prev) => prev.filter((p) => p.id !== id));
      toast.success("Foto eliminada");
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-cream">
      <header className="glass-header sticky top-0 z-40">
        <div className="px-4 sm:px-8 max-w-6xl mx-auto h-16 flex items-center justify-between">
          <p className="font-display text-xl font-semibold text-obsidian">
            ISI<span className="text-gold">'S</span> <span className="text-sm font-sans text-taupe tracking-widest uppercase">Panel</span>
          </p>
          <div className="flex items-center gap-2">
            <Link
              to="/"
              data-testid="admin-view-site-btn"
              className="inline-flex items-center gap-1.5 text-sm text-cocoa hover:text-gold px-3 py-2 transition-colors"
            >
              <ExternalLink className="w-4 h-4" /> Ver sitio
            </Link>
            <button
              onClick={onLogout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-1.5 text-sm text-cocoa hover:text-red-600 px-3 py-2 transition-colors"
            >
              <LogOut className="w-4 h-4" /> Salir
            </button>
          </div>
        </div>
      </header>

      <main className="px-4 sm:px-8 max-w-6xl mx-auto py-10">
        <div data-testid="admin-tabs" className="flex flex-wrap gap-2">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`rounded-full text-sm font-medium px-5 py-2.5 transition-colors duration-200 ${
                tab === t.id
                  ? "bg-gold text-white"
                  : "bg-creamcard border border-sand text-cocoa hover:border-gold/50"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "hoy" && (
          <div className="mt-8">
            <TodayPanel headers={headers} goTo={setTab} />
          </div>
        )}
        {tab === "citas" && (
          <div className="mt-8">
            <AppointmentsPanel headers={headers} />
          </div>
        )}
        {tab === "clientas" && (
          <div className="mt-8">
            <ClientsPanel headers={headers} />
          </div>
        )}
        {tab === "servicios" && (
          <div className="mt-8">
            <ServicesPanel headers={headers} />
          </div>
        )}
        {tab === "horario" && (
          <div className="mt-8">
            <SchedulePanel headers={headers} />
          </div>
        )}
        {tab === "resenas" && (
          <div className="mt-8">
            <ReviewsPanel headers={headers} />
          </div>
        )}
        {tab === "novedades" && (
          <div className="mt-8">
            <NewsPanel headers={headers} />
          </div>
        )}
        {tab === "telegram" && (
          <div className="mt-8">
            <TelegramPanel headers={headers} />
          </div>
        )}
        {tab === "calendario" && (
          <div className="mt-8">
            <CalendarPanel headers={headers} />
          </div>
        )}

        {tab === "fotos" && (
          <>
        <h1 className="font-display text-2xl sm:text-3xl font-semibold text-obsidian mt-8">Subir nuevo trabajo</h1>
        <p className="text-sm text-taupe mt-1">La foto aparecerá al instante en la galería pública de tu web.</p>

        <div className="mt-6 rounded-3xl bg-creamcard border border-sand p-6 sm:p-8">
          <div
            data-testid="admin-drag-drop-zone"
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); pickFile(e.dataTransfer.files?.[0]); }}
            className={`cursor-pointer rounded-2xl border-2 border-dashed p-8 sm:p-12 text-center transition-colors duration-200 ${
              dragOver ? "border-gold bg-rosesoft/50" : "border-sand hover:border-gold/60"
            }`}
          >
            {preview ? (
              <img src={preview} alt="Vista previa" className="mx-auto max-h-56 rounded-xl object-contain" />
            ) : (
              <>
                <ImagePlus className="w-10 h-10 text-gold mx-auto" />
                <p className="text-sm text-cocoa font-medium mt-4">Arrastra tu foto aquí o haz clic para elegirla</p>
                <p className="text-xs text-taupe mt-1">JPG, PNG o WebP · máx. 15 MB</p>
              </>
            )}
          </div>
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            data-testid="admin-file-input"
            className="hidden"
            onChange={(e) => pickFile(e.target.files?.[0])}
          />

          <div className="mt-5 flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              data-testid="admin-title-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Título del diseño (opcional): p. ej. «French dorado»"
              className="flex-1 rounded-xl border border-sand bg-cream px-4 py-3 text-sm text-obsidian placeholder:text-taupe/60 focus:outline-none focus:ring-2 focus:ring-gold"
            />
            <button
              onClick={upload}
              disabled={!file || uploading}
              data-testid="admin-upload-btn"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-gold hover:bg-gold-dark disabled:opacity-50 text-white text-sm font-medium px-8 py-3 transition-colors duration-200"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Publicar foto
            </button>
          </div>
        </div>

        <h2 className="font-display text-xl sm:text-2xl font-semibold text-obsidian mt-12">
          Fotos publicadas <span className="text-sm font-sans text-taupe font-normal">({photos.length})</span>
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 mt-6">
          {photos.map((p) => (
            <div
              key={p.id}
              data-testid={`admin-photo-item-${p.id}`}
              className="group relative rounded-2xl overflow-hidden border border-sand bg-creamcard"
            >
              <div className="relative h-40">
                <img src={p.url} alt={p.title || "Foto"} className="w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-obsidian/0 group-hover:bg-obsidian/50 transition-colors duration-200 flex items-center justify-center gap-2">
                  <button
                    onClick={() => startEdit(p)}
                    data-testid={`admin-edit-photo-btn-${p.id}`}
                    className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-full bg-white/90 hover:bg-gold hover:text-white text-cocoa p-2.5"
                    aria-label="Editar título"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => remove(p.id)}
                    data-testid={`admin-delete-photo-btn-${p.id}`}
                    className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-full bg-white/90 hover:bg-red-500 hover:text-white text-red-600 p-2.5"
                    aria-label="Eliminar foto"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              {editingId === p.id ? (
                <div className="px-2 py-2 flex items-center gap-1.5">
                  <input
                    type="text"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && saveEdit(p.id)}
                    data-testid={`admin-edit-title-input-${p.id}`}
                    autoFocus
                    className="w-full min-w-0 rounded-lg border border-sand bg-cream px-2 py-1.5 text-xs text-obsidian focus:outline-none focus:ring-2 focus:ring-gold"
                  />
                  <button
                    onClick={() => saveEdit(p.id)}
                    data-testid={`admin-edit-save-btn-${p.id}`}
                    className="shrink-0 rounded-lg bg-gold hover:bg-gold-dark text-white p-1.5"
                    aria-label="Guardar"
                  >
                    <Check className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => setEditingId(null)}
                    data-testid={`admin-edit-cancel-btn-${p.id}`}
                    className="shrink-0 rounded-lg border border-sand text-taupe hover:text-obsidian p-1.5"
                    aria-label="Cancelar"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <p className="text-xs text-taupe px-3 py-2 truncate">{p.title || "Sin título"}</p>
              )}
            </div>
          ))}
        </div>
          </>
        )}
      </main>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [checking, setChecking] = useState(!!token);

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
      })
      .finally(() => setChecking(false));
  }, [token]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    toast.success("Sesión cerrada");
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-gold animate-spin" />
      </div>
    );
  }

  return token ? <Dashboard token={token} onLogout={logout} /> : <Login onSuccess={setToken} />;
}
